import React, { Component } from "react";
import { Link } from "react-router-dom";

// CSS
import "../../styles/newDesign.scss";
import "./Home.scss";

// Components
import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";
import HomeBlocks from "../../components/HomeBlocks/HomeBlocks";

// Helper
import { apiFetch, resolveTenant, listCourseCatalog, listPlans, listInstructors, listMyMeetings, isLoggedIn } from "../../Helper/Api";
import { homeBlocksOf, homeContentOf, needsPlans, needsInstructors, needsMeetings } from "../../Helper/homeBlocks";
import { brandingOf, aboutOf } from "../../Helper/Branding";
import { t, forward } from "../../Helper/i18n";

// Properties
const properties = require("../../properties.json");

/** First free lesson that actually has a playable source, in priority order: a direct uploaded
 *  file (plays inline, no embed needed) beats a YouTube/Vimeo link (iframe embed) -- see
 *  LessonResponse.videoLinks/youtubeLinks/vimeoLinks. Lessons with none of the three (e.g. only
 *  images) are skipped rather than treated as "no intro video available". */
function findIntroVideo(lessons) {
  for (const lesson of lessons) {
    if (lesson.videoLinks && lesson.videoLinks[0]) {
      return { type: "file", src: lesson.videoLinks[0], title: lesson.lessonName };
    }
    if (lesson.youtubeLinks && lesson.youtubeLinks[0]) {
      const id = lesson.youtubeLinks[0].split("/").pop().replace("watch?v=", "");
      return { type: "embed", src: `https://www.youtube.com/embed/${id}?autoplay=1`, title: lesson.lessonName };
    }
    if (lesson.vimeoLinks && lesson.vimeoLinks[0]) {
      const id = lesson.vimeoLinks[0].split("/").pop();
      return { type: "embed", src: `https://player.vimeo.com/video/${id}?autoplay=1`, title: lesson.lessonName };
    }
  }
  return null;
}

class Home extends Component {
  state = {
    tenant: null,
    notFound: false,
    lessons: [],
    courses: [],
    plans: [],
    instructors: [],
    meetings: null, // the signed-in student's sessions, when the theme shows them
    introOpen: false,
  };

  componentDidMount() {
    resolveTenant()
      .then((tenant) => {
        const site = brandingOf(tenant).siteTitle || properties.site_name;
        document.title = tenant.name ? `${tenant.name} | ${site}` : site;
        this.setState({ tenant });
        // blocks of the theme (gym sites) need the plans and the trainers too; failing to get them just empties the block
        const blocks = homeBlocksOf(brandingOf(tenant));
        if (needsPlans(blocks, brandingOf(tenant))) listPlans(tenant.teacherUserId).then((plans) => this.setState({ plans: plans || [] })).catch(() => {});
        if (needsInstructors(blocks)) listInstructors(tenant.teacherUserId).then((instructors) => this.setState({ instructors: instructors || [] })).catch(() => {});
        if (needsMeetings(blocks) && isLoggedIn()) listMyMeetings().then((meetings) => this.setState({ meetings: meetings || [] })).catch(() => this.setState({ meetings: [] }));
        return Promise.all([
          apiFetch("/lessons/free", { query: { teacherUserId: tenant.teacherUserId } }),
          listCourseCatalog(tenant.teacherUserId),
        ]);
      })
      .then(([lessons, courses]) => this.setState({ lessons: lessons || [], courses: courses || [] }))
      .catch(() => this.setState({ notFound: true }));
  }

  openIntro = () => this.setState({ introOpen: true });
  closeIntro = () => this.setState({ introOpen: false });

  render() {
    const { tenant, notFound, lessons, courses, plans, instructors, meetings, introOpen } = this.state;

    if (notFound) {
      return (
        <div className="page">
          <div className="container text-center">
            <p>{t("notFound")}</p>
          </div>
        </div>
      );
    }

    if (!tenant) return null;

    const teacherName = tenant.name;
    const b = brandingOf(tenant);
    const heroTitle = b.heroTitle || t("heroTitle", { name: teacherName });
    const heroText = b.heroText || t("heroText");
    const showFeature = !!(b.featureTitle || b.featureText);
    const intro = findIntroVideo(lessons);
    const paths = courses.filter((c) => c.showOnHome);
    const aboutPhoto = (aboutOf(tenant) || {}).photo || null;
    const initials = String(teacherName || "")
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((w) => w.charAt(0))
      .join("")
      .toUpperCase();
    const blocks = homeBlocksOf(b);

    if (blocks) {
      return (
        <>
          <Header tenant={tenant} />
          <HomeBlocks blocks={blocks} tenant={tenant} branding={b} content={homeContentOf(b)}
            courses={courses} plans={plans} instructors={instructors} lessons={lessons} meetings={meetings} />
          <Footer tenant={tenant} />
        </>
      );
    }

    return (
      <>
        <Header tenant={tenant} />

        <div className="nd-page">
          <div className="home-hero">
            <div className="home-hero-copy">
              {b.heroEyebrow && <div className="nd-eyebrow">{b.heroEyebrow}</div>}
              <h1>{heroTitle}</h1>
              <p>{heroText}</p>
              <div className="home-hero-actions">
                <Link to="/courses" className="nd-btn-dark">{t("exploreCourses")} <span aria-hidden="true">{forward()}</span></Link>
                {intro && (
                  <button type="button" className="home-watch" onClick={this.openIntro}>
                    <span className="home-watch-circle">▶</span> {t("watchIntro")}
                  </button>
                )}
              </div>
              <div className="home-hero-auth">
                <Link to="/sign-up" className="nd-btn-ghost">
                  <i className="fas fa-user-plus"></i> {t("createAccount")}
                </Link>
                <Link to="/sign-in" className="nd-btn-ghost">
                  <i className="fas fa-sign-in-alt"></i> {t("login")}
                </Link>
              </div>
            </div>
            <div
              className={`home-hero-visual ${b.heroImageUrl ? "" : "no-image"}`}
              style={b.heroImageUrl ? { backgroundImage: `url(${b.heroImageUrl})`, backgroundSize: "cover", backgroundPosition: "center" } : undefined}
            >
              {b.tagline && <div className="home-hero-visual-label">{b.tagline}</div>}
            </div>
          </div>

          {paths.length > 0 && (
            <div className="nd-section nd-wrap">
              <div className="nd-eyebrow">{t("learningPaths")}</div>
              <h2 className="home-section-title nd-title">{t("choosePath")}</h2>
              <div className="home-path-grid">
                {paths.map((course, i) => (
                  <a href={`/courses/${course.id}`} className="home-path-card" key={course.id}>
                    <div
                      className="home-path-thumb"
                      style={course.image ? { backgroundImage: `url(${course.image})`, backgroundSize: "cover", backgroundPosition: "center" } : undefined}
                    >
                      <div className="home-path-num">{String(i + 1).padStart(2, "0")}</div>
                    </div>
                    <div className="home-path-body">
                      <div className="home-path-title-row"><h3>{course.title}</h3><span aria-hidden="true">{forward()}</span></div>
                      {course.description && <p>{course.description}</p>}
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}

          {showFeature && (
            <div className={`nd-section nd-wrap home-workflow ${b.featureImageUrl ? "" : "no-visual"}`}>
              {b.featureImageUrl && (
                <div
                  className="home-workflow-visual"
                  style={{ backgroundImage: `url(${b.featureImageUrl})`, backgroundSize: "cover", backgroundPosition: "center" }}
                ></div>
              )}
              <div className="home-workflow-copy">
                {b.featureTitle && <h2 className="nd-title">{b.featureTitle}</h2>}
                {b.featureText && <p>{b.featureText}</p>}
              </div>
            </div>
          )}

          {tenant.description && (
            <div className="nd-section nd-wrap home-about">
              <div className="home-about-text">
                <div className="nd-eyebrow">{t("aboutMe")}</div>
                <h2 className="nd-title">{t("aboutName", { name: teacherName })}</h2>
                <p>{tenant.description}</p>
              </div>
              {/* the teacher's own photo from their About page; without one, their initials on the theme's
                  colours -- this box used to stay an empty grey rectangle */}
              {aboutPhoto ? (
                <div className="home-about-media has-photo" style={{ backgroundImage: `url(${aboutPhoto})` }}></div>
              ) : (
                <div className="home-about-media is-mono" aria-hidden="true">
                  <span className="ring r1"></span>
                  <span className="ring r2"></span>
                  <b>{initials}</b>
                </div>
              )}
            </div>
          )}

        </div>

        {intro && (
          <div className={`nd-page home-intro-overlay ${introOpen ? "open" : ""}`} onClick={(e) => e.target === e.currentTarget && this.closeIntro()}>
            {introOpen && (
              <div className="home-intro-box">
                <button className="home-intro-close" onClick={this.closeIntro} aria-label={t("close")}>✕</button>
                {intro.type === "file" ? (
                  <video src={intro.src} controls autoPlay style={{ width: "100%", display: "block", borderRadius: 14 }} />
                ) : (
                  <iframe
                    src={intro.src}
                    title={intro.title}
                    allow="autoplay; fullscreen"
                    allowFullScreen
                    style={{ width: "100%", aspectRatio: "16/9", border: "none", borderRadius: 14, display: "block" }}
                  />
                )}
              </div>
            )}
          </div>
        )}

        <Footer tenant={tenant} />
      </>
    );
  }
}

export default Home;
